import { getApiErrorMessage, parseApiResponse } from '@/shared/utils/apiResponse';
import { parseSpreadsheetValidationError } from '@/shared/utils/parseSpreadsheetValidationError';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/shared/utils/requestBuilder';

import {
  type CobrancaDetalheDto,
  type CobrancaConsumoDto,
  type CobrancaDisparoDto,
  type CobrancaDto,
  type ConteudoDisparo,
  type CobrancaEtapaDto,
  type CobrancaEtapasDto,
  type CobrancaFinanceiroDto,
  type CobrancaListItemDto,
  type CobrancaLoteResultado,
  type CorrecaoNomesLoteResult,
  type CriarCobrancaInput,
  type ExportarCobrancasParams,
  type DuplicarReguaPayload,
  type EditarLoteItemInput,
  type EncerrarCobrancaInput,
  type IniciarLoteResult,
  type ListReguaCobrancasParams,
  type ListReguaTemplatesParams,
  type LoteItem,
  type LoteItemStatus,
  type MapeamentoLoteDto,
  type ReguaCobranca,
  type ReguaCobrancaConsumo,
  type ReguaCobrancaDetalhe,
  type ReguaCobrancaDisparo,
  type ReguaCobrancaDisparoFinanceiro,
  type ReenviarDisparoInput,
  type ReenvioDisparoResultado,
  type ReguaCobrancaEtapa,
  type ReguaCobrancaEtapas,
  type ReguaCobrancaExportEstado,
  type ReguaCobrancaFinanceiro,
  type ReguaCobrancaListResponse,
  type ReguaCobrancaLog,
  type ReguaCobrancaLogsEnvelope,
  type ReguaRequerente,
  type ReguaRequerenteDto,
  type ReguaTemplate,
  type ReguaTemplateListResponse,
  type ReguaCobrancaSituacao,
  type ReguaLoteAguardandoAtivacao,
  type ReguaLoteTempoRestante,
  type ReguaSituacaoCatalogo,
  type SalvarReguaPayload,
  type SalvarReguaResult,
  type ValidarInicioResult,
} from '../types';

const BASE = '/cobranca/regua-templates';
const COBRANCAS_BASE = '/cobranca/cobrancas';
const cobrancaPath = (id: string) => `${COBRANCAS_BASE}/${encodeURIComponent(id)}`;
// TODO: paginate the gallery when a workspace can exceed GALLERY_PAGE_SIZE templates.
const GALLERY_PAGE_SIZE = 100;

export const reguaTemplateService = {
  async list(params?: ListReguaTemplatesParams): Promise<ReguaTemplateListResponse> {
    const query: Record<string, string | number> = { page: 1, pageSize: GALLERY_PAGE_SIZE };
    const nome = params?.nome?.trim();
    if (nome) query.nome = nome;
    if (params?.status) query.status = params.status;
    return parseApiResponse<ReguaTemplateListResponse>(await apiGet(BASE, query));
  },

  async getById(id: string): Promise<ReguaTemplate> {
    return parseApiResponse<ReguaTemplate>(await apiGet(`${BASE}/${id}`));
  },

  async salvar(id: string, payload: SalvarReguaPayload): Promise<SalvarReguaResult> {
    return parseApiResponse<SalvarReguaResult>(await apiPut(`${BASE}/${id}`, payload));
  },

  async duplicar(id: string, payload: DuplicarReguaPayload): Promise<ReguaTemplate> {
    return parseApiResponse<ReguaTemplate>(await apiPost(`${BASE}/${id}/duplicar`, payload));
  },

  async excluir(id: string): Promise<void> {
    await parseApiResponse<undefined>(await apiDelete(`${BASE}/${id}`), { allowNoContent: true });
  },
};

export class CobrancaConflitoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CobrancaConflitoError';
  }
}

const CONFLITO_FALLBACK = 'Já existe uma cobrança ativa para este documento.';

function getApiErrorCode(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const error = (payload as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function toReguaCobranca(dto: CobrancaListItemDto): ReguaCobranca {
  return {
    id: dto.id,
    codigo: dto.codigo ?? dto.id.slice(0, 8).toUpperCase(),
    devedor: dto.devedor,
    documento: dto.documento,
    valor: Number(dto.valor),
    dataInicio: dto.dataInicio ?? dto.vencimento,
    vencimento: dto.vencimento,
    status: dto.status,
    situacaoDebito: dto.situacaoDebito,
    situacoes: dto.situacoes ?? [],
    templateId: dto.templateId ?? '',
    templateNome: dto.templateNome ?? '',
    resumo: dto.resumo ?? undefined,
  };
}

function toRequerente(dto: ReguaRequerenteDto | null): ReguaRequerente {
  return {
    documento: dto?.documento ?? '',
    nome: dto?.nome ?? '',
    email: dto?.email ?? '',
    telefone: dto?.telefone ?? '',
    oab: dto?.oab ?? '',
  };
}

function toReguaCobrancaDetalhe(dto: CobrancaDetalheDto): ReguaCobrancaDetalhe {
  return {
    id: dto.id,
    codigo: dto.codigo,
    devedor: dto.devedor,
    documento: dto.documento,
    valor: Number(dto.valor),
    dataInicio: dto.dataInicio,
    vencimento: dto.vencimento,
    status: dto.status,
    situacaoDebito: dto.situacaoDebito,
    situacoes: dto.situacoes ?? [],
    templateId: dto.templateId ?? '',
    templateNome: dto.templateNome ?? '',
    resumo: dto.resumo,
    devedorEmail: dto.devedorEmail ?? '',
    devedorTelefone: dto.devedorTelefone ?? '',
    templateDescricao: dto.templateDescricao ?? '',
    templateVersaoCriadaEm: dto.templateVersaoCriadaEm,
    timelineStepIndex: dto.timelineStepIndex,
    criadaPor: dto.criadaPor ?? '',
    requerente: toRequerente(dto.requerente),
    ultimaAcao: dto.ultimaAcao,
  };
}

function toDisparo(dto: CobrancaDisparoDto): ReguaCobrancaDisparo {
  return {
    id: dto.id,
    canal: dto.canal,
    destinatario: dto.destinatario ?? '',
    status: dto.status,
    creditos: dto.creditos ?? 0,
    custoPostagem: Number(dto.custoPostagem ?? 0),
    executadoEm: dto.executadoEm,
    eventos: dto.eventos ?? [],
    podeReenviar: dto.podeReenviar ?? false,
    reenvioDeId: dto.reenvioDeId ?? null,
  };
}

function toDisparoFinanceiro(dto: CobrancaDisparoDto): ReguaCobrancaDisparoFinanceiro {
  return { ...toDisparo(dto), etapaChave: dto.etapaChave ?? '' };
}

function toConsumo(dto: CobrancaConsumoDto | null | undefined): ReguaCobrancaConsumo | undefined {
  if (!dto) return undefined;
  return { creditos: dto.creditos ?? 0, custoPostagem: Number(dto.custoPostagem ?? 0) };
}

function toEtapa(dto: CobrancaEtapaDto): ReguaCobrancaEtapa {
  return {
    chave: dto.chave,
    nome: dto.nome,
    fase: dto.fase,
    dataPrevista: dto.dataPrevista,
    estado: dto.estado,
    canais: dto.canais ?? [],
    disparos: (dto.disparos ?? []).map(toDisparo),
    marcador: dto.marcador,
    suspensa: dto.suspensa ?? false,
    suspensaoAberta: dto.suspensaoAberta ?? null,
    podeSuspender: dto.podeSuspender ?? false,
    podeRetomar: dto.podeRetomar ?? false,
    consumo: toConsumo(dto.consumo),
  };
}

const EXPORT_BASE = `${COBRANCAS_BASE}/export`;

export type CobrancaAcaoLote = 'pausar' | 'retomar' | 'registrar-pagamento' | 'encerrar';

// A lista e o export mandam exatamente os mesmos filtros — o backend usa o mesmo whereBuilder
// para os dois, então divergir aqui faria a planilha não bater com a tela.
function buildCobrancaFiltrosQuery(
  params: Omit<ListReguaCobrancasParams, 'page' | 'pageSize'>,
): Record<string, string> {
  const query: Record<string, string> = {};
  if (params.termo?.trim()) query.search = params.termo.trim();
  if (params.status?.length) query.status = params.status.join(',');
  if (params.situacao?.length) query.situacao = params.situacao.join(',');
  if (params.templateId?.length) query.templateId = params.templateId.join(',');
  if (params.fase?.length) query.fase = params.fase.join(',');
  if (params.canal?.length) query.canal = params.canal.join(',');
  if (params.sortBy) {
    query.sortBy = params.sortBy;
    query.sortOrder = params.sortOrder ?? 'asc';
  }
  return query;
}

export const reguaCobrancaService = {
  async list(params: ListReguaCobrancasParams): Promise<ReguaCobrancaListResponse> {
    const paginado = await parseApiResponse<{ items: CobrancaListItemDto[]; total: number }>(
      await apiGet(COBRANCAS_BASE, {
        page: params.page,
        pageSize: params.pageSize,
        ...buildCobrancaFiltrosQuery(params),
      }),
    );
    return { items: paginado.items.map(toReguaCobranca), total: paginado.total };
  },

  async exportar(params: ExportarCobrancasParams): Promise<string> {
    const { exportId } = await parseApiResponse<{ exportId: string }>(
      await apiPost(EXPORT_BASE, { ...buildCobrancaFiltrosQuery(params), formato: params.formato }),
    );
    return exportId;
  },

  async statusExport(exportId: string, signal?: AbortSignal): Promise<ReguaCobrancaExportEstado> {
    return parseApiResponse<ReguaCobrancaExportEstado>(
      await apiGet(`${EXPORT_BASE}/${encodeURIComponent(exportId)}`, undefined, false, signal),
    );
  },

  async registrarAcordo(id: string): Promise<void> {
    await parseApiResponse<unknown>(await apiPost(`${cobrancaPath(id)}/registrar-acordo`), {
      allowNoContent: true,
    });
  },

  async aplicarEmLote(
    acao: CobrancaAcaoLote,
    ids: string[],
    motivo?: string,
    detalhes?: string,
  ): Promise<number> {
    const body = { ids, ...(motivo ? { motivo } : {}), ...(detalhes ? { detalhes } : {}) };
    const { afetadas } = await parseApiResponse<CobrancaLoteResultado>(
      await apiPost(`${COBRANCAS_BASE}/bulk/${acao}`, body),
    );
    return afetadas;
  },

  async criar(input: CriarCobrancaInput): Promise<CobrancaDto> {
    const response = await apiPost(COBRANCAS_BASE, input);
    if (response.status === 409) {
      const payload = await response.json().catch(() => null);
      const message = getApiErrorMessage(payload, CONFLITO_FALLBACK);
      if (getApiErrorCode(payload) === 'COBRANCA_DUPLICADA') {
        throw new CobrancaConflitoError(message);
      }
      throw new Error(message);
    }
    return parseApiResponse<CobrancaDto>(response);
  },

  async getById(id: string): Promise<ReguaCobrancaDetalhe> {
    const dto: CobrancaDetalheDto | undefined = await parseApiResponse<CobrancaDetalheDto>(
      await apiGet(cobrancaPath(id)),
    );
    if (!dto) throw new Error('Cobrança não encontrada');
    return toReguaCobrancaDetalhe(dto);
  },

  async etapas(id: string): Promise<ReguaCobrancaEtapas> {
    const dto = await parseApiResponse<CobrancaEtapasDto>(
      await apiGet(`${cobrancaPath(id)}/etapas`),
    );
    return {
      etapaAtual: dto?.etapaAtual ?? null,
      etapas: (dto?.etapas ?? []).map(toEtapa),
      consumoTotal: toConsumo(dto?.consumoTotal),
    };
  },

  async logs(id: string): Promise<ReguaCobrancaLog[]> {
    const dto = await parseApiResponse<ReguaCobrancaLogsEnvelope>(
      await apiGet(`${cobrancaPath(id)}/logs`),
    );
    return dto?.itens ?? [];
  },

  async financeiro(id: string): Promise<ReguaCobrancaFinanceiro> {
    const dto = await parseApiResponse<CobrancaFinanceiroDto>(
      await apiGet(`${cobrancaPath(id)}/financeiro`),
    );
    return {
      disparos: (dto?.disparos ?? []).map(toDisparoFinanceiro),
      totais: {
        creditos: dto?.totais?.creditos ?? 0,
        custoPostagem: Number(dto?.totais?.custoPostagem ?? 0),
        quantidadeDisparos: dto?.totais?.quantidadeDisparos ?? 0,
      },
    };
  },

  async pausar(id: string, motivo?: string): Promise<void> {
    await parseApiResponse<unknown>(
      await apiPost(`${cobrancaPath(id)}/pausar`, motivo ? { motivo } : {}),
      { allowNoContent: true },
    );
  },

  async retomar(id: string): Promise<void> {
    await parseApiResponse<unknown>(await apiPost(`${cobrancaPath(id)}/retomar`), {
      allowNoContent: true,
    });
  },

  async registrarPagamento(id: string): Promise<void> {
    await parseApiResponse<unknown>(await apiPost(`${cobrancaPath(id)}/registrar-pagamento`), {
      allowNoContent: true,
    });
  },

  async encerrar(id: string, input: EncerrarCobrancaInput): Promise<void> {
    await parseApiResponse<unknown>(await apiPost(`${cobrancaPath(id)}/encerrar`, input), {
      allowNoContent: true,
    });
  },

  async suspenderEtapa(id: string, etapaChave: string, motivo?: string): Promise<void> {
    await parseApiResponse<unknown>(
      await apiPost(
        `${cobrancaPath(id)}/etapas/${encodeURIComponent(etapaChave)}/suspender`,
        motivo ? { motivo } : {},
      ),
      { allowNoContent: true },
    );
  },

  async retomarEtapa(id: string, etapaChave: string): Promise<void> {
    await parseApiResponse<unknown>(
      await apiPost(`${cobrancaPath(id)}/etapas/${encodeURIComponent(etapaChave)}/retomar`),
      { allowNoContent: true },
    );
  },

  async reenviarDisparo(
    id: string,
    disparoId: string,
    input: ReenviarDisparoInput,
  ): Promise<ReenvioDisparoResultado> {
    return parseApiResponse<ReenvioDisparoResultado>(
      await apiPost(
        `${cobrancaPath(id)}/disparos/${encodeURIComponent(disparoId)}/reenviar`,
        input,
      ),
    );
  },

  // Sem snapshot o back devolve 200 `{ indisponivel: true }` — a prévia mostra o aviso em vez de
  // tratar como erro.
  async conteudoDisparo(id: string, disparoId: string): Promise<ConteudoDisparo> {
    return parseApiResponse<ConteudoDisparo>(
      await apiGet(`${cobrancaPath(id)}/disparos/${encodeURIComponent(disparoId)}/conteudo`),
    );
  },
};

const LOTES_BASE = '/cobranca/lotes';

// TODO(back): validar-inicio e iniciar são o contrato assumido (verbo/rota) até o backend
// do lote existir — confirmar ao integrar.
export const reguaLoteService = {
  async criar({ nome }: { nome: string }): Promise<{ loteId: string }> {
    return parseApiResponse<{ loteId: string }>(await apiPost(LOTES_BASE, { nome }));
  },

  async configurar(id: string, { reguaTemplateId }: { reguaTemplateId: string }): Promise<void> {
    await parseApiResponse<undefined>(
      await apiPatch(`${LOTES_BASE}/${id}/configuracao`, { reguaTemplateId }),
      { allowNoContent: true },
    );
  },

  async upload({
    id,
    file,
    mapeamento,
    ignoreRows,
  }: {
    id: string;
    file: File;
    mapeamento?: MapeamentoLoteDto;
    ignoreRows?: number[];
  }): Promise<{ loteId: string }> {
    const fd = new FormData();
    if (mapeamento) fd.append('mapeamento', JSON.stringify(mapeamento));
    if (ignoreRows?.length) fd.append('ignoreRows', JSON.stringify(ignoreRows));
    fd.append('file', file);

    const res = await apiPost(`${LOTES_BASE}/${id}/upload`, fd);
    if (!res.ok) {
      const validationError = await parseSpreadsheetValidationError(res);
      if (validationError) throw validationError;
      const payload = await res.json().catch(() => null);
      throw new Error(getApiErrorMessage(payload, 'Erro ao enviar planilha'));
    }
    return parseApiResponse<{ loteId: string }>(res);
  },

  async listarItens(
    id: string,
    { page, limit, status }: { page: number; limit: number; status?: LoteItemStatus },
  ): Promise<{
    items: LoteItem[];
    total: number;
    corrigiveis: number;
    page: number;
    limit: number;
  }> {
    const query: Record<string, string | number> = { page, limit };
    if (status) query.status = status;
    return parseApiResponse<{
      items: LoteItem[];
      total: number;
      corrigiveis: number;
      page: number;
      limit: number;
    }>(await apiGet(`${LOTES_BASE}/${id}/itens`, query));
  },

  async atualizarItem(itemId: string, input: EditarLoteItemInput): Promise<void> {
    await parseApiResponse<undefined>(await apiPut(`${LOTES_BASE}/itens/${itemId}`, input), {
      allowNoContent: true,
    });
  },

  async removerItem(id: string, itemId: string): Promise<void> {
    await parseApiResponse<undefined>(
      await apiPost(`${LOTES_BASE}/${id}/itens/${itemId}/remover`, {}),
      { allowNoContent: true },
    );
  },

  async validarInicio(id: string): Promise<ValidarInicioResult> {
    return parseApiResponse<ValidarInicioResult>(
      await apiGet(`${LOTES_BASE}/${id}/validar-inicio`),
    );
  },

  async corrigirNomes(id: string): Promise<CorrecaoNomesLoteResult> {
    return parseApiResponse<CorrecaoNomesLoteResult>(
      await apiPost(`${LOTES_BASE}/${id}/corrigir-nomes`, {}),
    );
  },

  async iniciar(id: string, { aceiteSedex }: { aceiteSedex: boolean }): Promise<IniciarLoteResult> {
    return parseApiResponse<IniciarLoteResult>(
      await apiPost(`${LOTES_BASE}/${id}/iniciar`, { aceiteSedex }),
    );
  },

  async listarAguardandoAtivacao(): Promise<ReguaLoteAguardandoAtivacao[]> {
    const res = await parseApiResponse<{
      items: Array<{ id: string; nome: string; totais?: { validas?: number } | null }>;
    }>(await apiGet(LOTES_BASE, { status: 'AGUARDANDO_ATIVACAO', page: 1, pageSize: 20 }));

    return res.items.map((lote) => ({
      id: lote.id,
      nome: lote.nome,
      totalCobrancas: lote.totais?.validas ?? 0,
    }));
  },

  async tempoRestante(id: string): Promise<ReguaLoteTempoRestante> {
    return parseApiResponse<ReguaLoteTempoRestante>(
      await apiGet(`${LOTES_BASE}/${id}/tempo-restante`),
    );
  },

  async cancelar(id: string, motivo?: string): Promise<void> {
    await parseApiResponse<unknown>(
      await apiPost(`${LOTES_BASE}/${id}/cancelar`, motivo ? { motivo } : {}),
      { allowNoContent: true },
    );
  },
};

const SITUACOES_BASE = '/cobranca/situacoes';
const situacoesDaCobrancaPath = (id: string) => `${cobrancaPath(id)}/situacoes`;

export const reguaSituacaoService = {
  async listarCatalogo(): Promise<ReguaSituacaoCatalogo[]> {
    const res = await parseApiResponse<{ items: ReguaSituacaoCatalogo[] }>(
      await apiGet(SITUACOES_BASE, { active: true }),
    );
    return res.items;
  },

  async vincular(
    id: string,
    situacaoIds: string[],
    desativarAnteriores = false,
  ): Promise<ReguaCobrancaSituacao[]> {
    const res = await parseApiResponse<{ items: ReguaCobrancaSituacao[] }>(
      await apiPost(situacoesDaCobrancaPath(id), { situacaoIds, desativarAnteriores }),
    );
    return res.items;
  },

  async desvincular(id: string, situacaoId: string): Promise<ReguaCobrancaSituacao[]> {
    const res = await parseApiResponse<{ items: ReguaCobrancaSituacao[] }>(
      await apiDelete(`${situacoesDaCobrancaPath(id)}/${encodeURIComponent(situacaoId)}`),
    );
    return res.items;
  },
};
